(function () {
  const KIND = {
    store: { label: 'Store event', cssVar: '--event-color-store' },
    industry: { label: 'Industry event', cssVar: '--event-color-industry' },
    series: { label: 'Weekly event', cssVar: '--event-color-series' }
  };

  const WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
  const MONTHS = [
    'January',
    'February',
    'March',
    'April',
    'May',
    'June',
    'July',
    'August',
    'September',
    'October',
    'November',
    'December'
  ];

  function parseJSONScript(el, selector) {
    const node = el.querySelector(selector);
    if (!node) return [];
    try {
      return JSON.parse(node.textContent.trim() || '[]');
    } catch (e) {
      console.warn('[events-calendar] failed to parse', selector, e);
      return [];
    }
  }

  function startOfDay(d) {
    const x = new Date(d);
    x.setHours(0, 0, 0, 0);
    return x;
  }

  function sameDay(a, b) {
    return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
  }

  function dateKey(d) {
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
  }

  function parseLocalDate(str) {
    // YYYY-MM-DD as a local date (not UTC midnight)
    if (!str) return null;
    const [y, m, d] = str.split('-').map(Number);
    if (!y || !m || !d) return null;
    return new Date(y, m - 1, d);
  }

  function parseDateTime(str) {
    if (!str) return null;
    const d = new Date(str);
    return isNaN(d.getTime()) ? null : d;
  }

  function formatDate(d, opts) {
    return d.toLocaleDateString(undefined, opts || { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' });
  }

  function formatTime(d) {
    return d.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' });
  }

  function formatTimeRange(start, end) {
    if (!start) return '';
    if (!end || sameDay(start, end) === false) return formatTime(start);
    if (start.getTime() === end.getTime()) return formatTime(start);
    return `${formatTime(start)} – ${formatTime(end)}`;
  }

  // Detail-pane label: "Date @ Time to Date @ Time" — collapses to
  // "Date @ Time to Time" on a single day, or "Date @ Time" with no end.
  function formatDateTimeRange(start, end) {
    if (!start) return '';
    const dateOpts = { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' };
    const startStr = `${formatDate(start, dateOpts)} @ ${formatTime(start)}`;
    if (!end || start.getTime() === end.getTime()) return startStr;
    if (sameDay(start, end)) return `${startStr} to ${formatTime(end)}`;
    return `${startStr} to ${formatDate(end, dateOpts)} @ ${formatTime(end)}`;
  }

  // Expand a series into occurrences within [rangeStart, rangeEnd].
  // start_at carries the first day + each occurrence's start time;
  // end_at carries the last day + each occurrence's end time.
  // Recurrence may be 'weekly' (default), 'bi-weekly', or 'monthly' (Nth weekday of month).
  function expandSeries(series, rangeStart, rangeEnd) {
    const out = [];
    const seriesStartDT = parseDateTime(series.start_at);
    const seriesEndDT = parseDateTime(series.end_at);
    if (!seriesStartDT || !seriesEndDT) return out;

    const startHH = seriesStartDT.getHours();
    const startMM = seriesStartDT.getMinutes();
    const endHH = seriesEndDT.getHours();
    const endMM = seriesEndDT.getMinutes();

    const seriesStartDay = startOfDay(seriesStartDT);
    const seriesEndDay = startOfDay(seriesEndDT);

    const recurrence = (series.recurrence || 'weekly').toLowerCase();
    const weekday = seriesStartDay.getDay();
    const nth = Math.floor((seriesStartDay.getDate() - 1) / 7) + 1;

    function nthWeekdayOfMonth(year, month, n, dow) {
      const first = new Date(year, month, 1);
      const offset = (dow - first.getDay() + 7) % 7;
      const day = 1 + offset + (n - 1) * 7;
      const lastDay = new Date(year, month + 1, 0).getDate();
      return day > lastDay ? null : new Date(year, month, day);
    }

    function nextOccurrence(d) {
      if (recurrence === 'bi-weekly') return new Date(d.getTime() + 14 * 86400000);
      if (recurrence === 'monthly') {
        let y = d.getFullYear();
        let m = d.getMonth() + 1;
        // Cap the search so a bad config can't loop forever.
        for (let i = 0; i < 24; i++) {
          if (m > 11) { m = 0; y++; }
          const next = nthWeekdayOfMonth(y, m, nth, weekday);
          if (next) return next;
          m++;
        }
        return null;
      }
      return new Date(d.getTime() + 7 * 86400000);
    }

    let cursor = new Date(seriesStartDay);
    while (cursor < rangeStart && cursor <= seriesEndDay) {
      const stepped = nextOccurrence(cursor);
      if (!stepped) return out;
      cursor = stepped;
    }

    while (cursor <= rangeEnd && cursor <= seriesEndDay) {
      const start = new Date(cursor);
      start.setHours(startHH, startMM, 0, 0);
      const end = new Date(cursor);
      end.setHours(endHH, endMM, 0, 0);
      out.push({
        kind: 'series',
        id: `${series.id}::${dateKey(cursor)}`,
        seriesId: series.id,
        title: series.title,
        description: series.description_text || '',
        location: series.default_location_name || '',
        category: series.default_category || '',
        start,
        end,
        url: series.url || '',
        linkLabel: 'About this series'
      });
      const stepped = nextOccurrence(cursor);
      if (!stepped) break;
      cursor = stepped;
    }
    return out;
  }

  function normalizeStore(e) {
    const start = parseDateTime(e.start_at);
    const end = parseDateTime(e.end_at);
    return {
      kind: 'store',
      id: String(e.id),
      seriesId: e.series_id == null ? null : String(e.series_id),
      title: e.title,
      subtitle: e.subtitle || '',
      description: e.short_description || '',
      location: e.location_name || '',
      category: e.category || '',
      start,
      end,
      url: e.url || '',
      linkLabel: 'View event details'
    };
  }

  function normalizeIndustry(e) {
    const start = parseDateTime(e.start_at);
    const end = parseDateTime(e.end_at);
    const loc = [e.location_city, e.location_country].filter(Boolean).join(', ');
    return {
      kind: 'industry',
      id: String(e.id),
      title: e.title,
      description: e.description || '',
      location: loc,
      category: '',
      start,
      end,
      url: e.official_url || '',
      linkLabel: 'Official site',
      accentColor: e.accent_color || null
    };
  }

  // Does an event fall on `day` (local)?
  function eventOnDay(ev, day) {
    if (!ev.start) return false;
    const d0 = startOfDay(day);
    const d1 = new Date(d0);
    d1.setDate(d1.getDate() + 1);
    const s = startOfDay(ev.start);
    const e = ev.end ? startOfDay(ev.end) : s;
    return s < d1 && e >= d0;
  }

  class EventsCalendar extends HTMLElement {
    connectedCallback() {
      this.storeEvents = parseJSONScript(this, '[data-events-store]').map(normalizeStore);
      this.industryEvents = parseJSONScript(this, '[data-events-industry]').map(normalizeIndustry);
      this.seriesList = parseJSONScript(this, '[data-events-series]').filter((s) => s.active !== false);

      this.monthEl = this.querySelector('[data-calendar-month]');
      this.agendaEl = this.querySelector('[data-calendar-agenda]');
      this.titleEl = this.querySelector('[data-calendar-title]');
      this.detailEl = this.querySelector('[data-event-detail]');

      const today = new Date();
      this.viewYear = today.getFullYear();
      this.viewMonth = today.getMonth();
      this.selectedId = null;

      // Legend toggles: which event kinds are currently shown.
      this.activeKinds = new Set(['store', 'industry', 'series']);
      this.querySelectorAll('[data-legend-kind]').forEach((b) =>
        b.addEventListener('click', () => this.toggleKind(b.dataset.legendKind, b))
      );

      // Location toggles: which locations are currently shown. Industry events
      // are global (city, country) and never participate — see matchesLocation.
      this.locationButtons = Array.from(this.querySelectorAll('[data-location-filter]'));
      this.activeLocations = new Set(this.locationButtons.map((b) => b.dataset.locationFilter));
      this.allLocationCount = this.locationButtons.length;
      this.locationButtons.forEach((b) =>
        b.addEventListener('click', () => this.toggleLocation(b.dataset.locationFilter, b))
      );

      this.querySelectorAll('[data-cal-prev]').forEach((b) => b.addEventListener('click', () => this.shiftMonth(-1)));
      this.querySelectorAll('[data-cal-next]').forEach((b) => b.addEventListener('click', () => this.shiftMonth(1)));
      this.querySelectorAll('[data-cal-today]').forEach((b) => b.addEventListener('click', () => this.goToToday()));
      this.querySelectorAll('[data-cal-view]').forEach((b) =>
        b.addEventListener('click', () => this.setView(b.dataset.calView))
      );

      // Default to agenda on narrow viewports; month elsewhere.
      this.setView(window.matchMedia('(max-width: 640px)').matches ? 'agenda' : 'month');

      this.render();
    }

    shiftMonth(delta) {
      const next = new Date(this.viewYear, this.viewMonth + delta, 1);
      this.viewYear = next.getFullYear();
      this.viewMonth = next.getMonth();
      this.render();
    }

    goToToday() {
      const t = new Date();
      this.viewYear = t.getFullYear();
      this.viewMonth = t.getMonth();
      this.render();
    }

    toggleKind(kind, btn) {
      if (!kind || !KIND[kind]) return;
      if (this.activeKinds.has(kind)) this.activeKinds.delete(kind);
      else this.activeKinds.add(kind);
      const active = this.activeKinds.has(kind);
      if (btn) btn.setAttribute('aria-pressed', String(active));
      this.render();
    }

    toggleLocation(key, btn) {
      if (!key) return;
      if (this.activeLocations.has(key)) this.activeLocations.delete(key);
      else this.activeLocations.add(key);
      if (btn) btn.setAttribute('aria-pressed', String(this.activeLocations.has(key)));
      this.render();
    }

    matchesLocation(occ) {
      if (!this.allLocationCount) return true; // setting left blank
      // Nothing narrowed — everything shows, including events whose location
      // name mentions none of the filters.
      if (this.activeLocations.size === this.allLocationCount) return true;
      if (occ.kind === 'industry') return true; // industry events are global
      const loc = (occ.location || '').toLowerCase();
      for (const key of this.activeLocations) {
        if (loc.indexOf(key) !== -1) return true;
      }
      return false;
    }

    get locationsNarrowed() {
      return this.allLocationCount > 0 && this.activeLocations.size !== this.allLocationCount;
    }

    setView(view) {
      if (view !== 'month' && view !== 'agenda') return;
      this.dataset.view = view;
      this.querySelectorAll('[data-cal-view]').forEach((b) => {
        const active = b.dataset.calView === view;
        b.setAttribute('aria-selected', String(active));
        b.classList.toggle('is-active', active);
      });
    }

    occurrencesForRange(start, end) {
      const seriesOccurrences = [];
      this.seriesList.forEach((s) => {
        seriesOccurrences.push(...expandSeries(s, start, end));
      });

      // Override pattern: if a store_event references the series and falls on the
      // same date as a series occurrence, drop the auto-generated occurrence.
      const overrideKeys = new Set();
      this.storeEvents.forEach((e) => {
        if (e.seriesId && e.start) {
          overrideKeys.add(`${e.seriesId}::${dateKey(e.start)}`);
        }
      });
      const filteredSeries = seriesOccurrences.filter((o) => !overrideKeys.has(o.id));

      const all = [
        ...this.storeEvents.filter((e) => e.start && e.start <= end && (e.end || e.start) >= start),
        ...this.industryEvents.filter((e) => e.start && e.start <= end && (e.end || e.start) >= start),
        ...filteredSeries
      ].filter((o) => this.activeKinds.has(o.kind) && this.matchesLocation(o));
      all.sort((a, b) => a.start - b.start);
      return all;
    }

    render() {
      const monthStart = new Date(this.viewYear, this.viewMonth, 1);
      const monthEnd = new Date(this.viewYear, this.viewMonth + 1, 0);
      monthEnd.setHours(23, 59, 59, 999);

      // Calendar grid range: include leading + trailing days to fill weeks
      const gridStart = new Date(monthStart);
      gridStart.setDate(gridStart.getDate() - gridStart.getDay());
      const gridEnd = new Date(monthEnd);
      gridEnd.setDate(gridEnd.getDate() + (6 - gridEnd.getDay()));

      const occurrences = this.occurrencesForRange(gridStart, gridEnd);
      this.currentOccurrences = occurrences;

      if (this.titleEl) {
        this.titleEl.textContent = `${MONTHS[this.viewMonth]} ${this.viewYear}`;
      }
      this.renderMonth(occurrences, gridStart, gridEnd, monthStart);
      this.renderAgenda(occurrences.filter((o) => o.start >= monthStart && o.start <= monthEnd));

      // Re-apply selection if still present in the new range
      if (this.selectedId) {
        const match = occurrences.find((o) => o.id === this.selectedId);
        if (match) this.showDetail(match);
        else this.hideDetail();
      }
    }

    renderMonth(occurrences, gridStart, gridEnd, monthStart) {
      if (!this.monthEl) return;
      const today = startOfDay(new Date());

      // Bucket events by date key. Multi-day events are added to every day they
      // span so they can render as a continuous bar across the month grid.
      const byDate = new Map();
      const sorted = [...occurrences].sort((a, b) => a.start - b.start);
      sorted.forEach((occ) => {
        if (!occ.start) return;
        const startDay = startOfDay(occ.start);
        const endDay = occ.end ? startOfDay(occ.end) : startDay;
        const spanStart = startDay > gridStart ? startDay : startOfDay(gridStart);
        const spanEnd = endDay < gridEnd ? endDay : startOfDay(gridEnd);
        const cur = new Date(spanStart);
        while (cur <= spanEnd) {
          const k = dateKey(cur);
          if (!byDate.has(k)) byDate.set(k, []);
          byDate.get(k).push({
            occ,
            isStart: sameDay(cur, startDay),
            isEnd: sameDay(cur, endDay),
            isWeekStart: cur.getDay() === 0
          });
          cur.setDate(cur.getDate() + 1);
        }
      });

      const head = `
        <div class="events-calendar__weekdays">
          ${WEEKDAYS.map((w) => `<div class="events-calendar__weekday">${w}</div>`).join('')}
        </div>
      `;

      const cells = [];
      const cursor = new Date(gridStart);
      while (cursor <= gridEnd) {
        const inMonth = cursor.getMonth() === monthStart.getMonth();
        const isToday = sameDay(cursor, today);
        const k = dateKey(cursor);
        const items = byDate.get(k) || [];
        const visible = items.slice(0, 3);
        const overflow = items.length - visible.length;
        const pills = visible
          .map((entry) => {
            const o = entry.occ;
            const continuesLeft = !entry.isStart;
            const continuesRight = !entry.isEnd;
            const showLabel = entry.isStart || entry.isWeekStart;
            const cls = ['events-calendar__pill'];
            if (continuesLeft || continuesRight) cls.push('events-calendar__pill--span');
            if (!showLabel) cls.push('events-calendar__pill--thin');
            return `
              <button
                type="button"
                class="${cls.join(' ')}"
                data-occ-id="${o.id}"
                title="${escapeHtml(o.title)}"
                style="--pill-color: var(${KIND[o.kind].cssVar});"
              >
                ${showLabel ? '<span class="events-calendar__pill-dot" aria-hidden="true"></span>' : ''}
                <span class="events-calendar__pill-title">${showLabel ? escapeHtml(o.title) : ''}</span>
              </button>`;
          })
          .join('');
        const overflowEl =
          overflow > 0
            ? `<button type="button" class="events-calendar__overflow" data-occ-overflow="${k}">+${overflow} more</button>`
            : '';
        cells.push(`
          <div class="events-calendar__day${inMonth ? '' : ' is-out'}${isToday ? ' is-today' : ''}">
            <div class="events-calendar__day-num">${cursor.getDate()}</div>
            <div class="events-calendar__day-list">${pills}${overflowEl}</div>
          </div>
        `);
        cursor.setDate(cursor.getDate() + 1);
      }

      this.monthEl.innerHTML = head + `<div class="events-calendar__grid">${cells.join('')}</div>`;

      this.monthEl.querySelectorAll('[data-occ-id]').forEach((btn) => {
        btn.addEventListener('click', () => {
          const occ = this.currentOccurrences.find((o) => o.id === btn.dataset.occId);
          if (occ) this.select(occ);
        });
      });
      this.monthEl.querySelectorAll('[data-occ-overflow]').forEach((btn) => {
        btn.addEventListener('click', () => {
          // Switch to agenda view so overflowed events become reachable
          this.setView('agenda');
          const target = this.agendaEl?.querySelector(`[data-agenda-date="${btn.dataset.occOverflow}"]`);
          if (target && typeof target.scrollIntoView === 'function') {
            target.scrollIntoView({ behavior: 'smooth', block: 'center' });
          }
        });
      });
    }

    renderAgenda(occurrences) {
      if (!this.agendaEl) return;
      if (!occurrences.length) {
        const msg = this.locationsNarrowed
          ? 'No events this month at the selected locations.'
          : 'No events this month.';
        this.agendaEl.innerHTML = `<p class="events-calendar__empty">${msg}</p>`;
        return;
      }
      const rows = occurrences
        .map((o) => {
          const dateOpts = { weekday: 'short', month: 'short', day: 'numeric' };
          const startLabel = formatDate(o.start, dateOpts);
          const isMultiDay = o.end && !sameDay(o.start, o.end);
          const endLabel = isMultiDay ? formatDate(o.end, dateOpts) : '';
          const timeLabel = formatTimeRange(o.start, o.end);
          return `
            <button
              type="button"
              class="events-calendar__agenda-row"
              data-occ-id="${o.id}"
              data-agenda-date="${dateKey(o.start)}"
              style="--pill-color: var(${KIND[o.kind].cssVar});"
            >
              <span class="events-calendar__agenda-date">
                <span>${escapeHtml(startLabel)}</span>
                ${isMultiDay ? `<span class="events-calendar__agenda-date-end">to ${escapeHtml(endLabel)}</span>` : ''}
              </span>
              <span class="events-calendar__agenda-dot" aria-hidden="true"></span>
              <span class="events-calendar__agenda-body">
                <span class="events-calendar__agenda-title">${escapeHtml(o.title)}</span>
                <span class="events-calendar__agenda-meta">
                  ${timeLabel ? escapeHtml(timeLabel) : ''}${o.location ? (timeLabel ? ' · ' : '') + escapeHtml(o.location) : ''}
                </span>
              </span>
            </button>`;
        })
        .join('');
      this.agendaEl.innerHTML = rows;
      this.agendaEl.querySelectorAll('[data-occ-id]').forEach((btn) => {
        btn.addEventListener('click', () => {
          const occ = this.currentOccurrences.find((o) => o.id === btn.dataset.occId);
          if (occ) this.select(occ);
        });
      });
    }

    select(occ) {
      this.selectedId = occ.id;
      this.querySelectorAll('.events-calendar__pill.is-selected, .events-calendar__agenda-row.is-selected').forEach(
        (el) => el.classList.remove('is-selected')
      );
      this.querySelectorAll(`[data-occ-id="${cssEscape(occ.id)}"]`).forEach((el) => el.classList.add('is-selected'));
      this.showDetail(occ);
      if (this.detailEl && typeof this.detailEl.scrollIntoView === 'function') {
        const rect = this.detailEl.getBoundingClientRect();
        const vh = window.innerHeight || document.documentElement.clientHeight;
        if (rect.top < 0 || rect.bottom > vh) {
          this.detailEl.scrollIntoView({ behavior: 'smooth', block: 'end' });
        }
      }
    }

    showDetail(occ) {
      if (!this.detailEl) return;
      const meta = KIND[occ.kind];
      const dot = this.detailEl.querySelector('[data-detail-dot]');
      if (dot) dot.style.backgroundColor = `var(${meta.cssVar})`;
      const setText = (sel, text) => {
        const n = this.detailEl.querySelector(sel);
        if (n) n.textContent = text || '';
      };
      setText('[data-detail-kind]', meta.label);
      setText('[data-detail-title]', occ.title);
      setText('[data-detail-when]', formatDateTimeRange(occ.start, occ.end));

      const locWrap = this.detailEl.querySelector('[data-detail-location-wrap]');
      const locEl = this.detailEl.querySelector('[data-detail-location]');
      if (locWrap && locEl) {
        if (occ.location) {
          locEl.textContent = occ.location;
          locWrap.hidden = false;
        } else {
          locWrap.hidden = true;
        }
      }

      const desc = this.detailEl.querySelector('[data-detail-description]');
      if (desc) {
        if (occ.description) {
          desc.textContent = occ.description;
          desc.hidden = false;
        } else {
          desc.hidden = true;
        }
      }

      const link = this.detailEl.querySelector('[data-detail-link]');
      const linkLabel = this.detailEl.querySelector('[data-detail-link-label]');
      if (link) {
        if (occ.url) {
          link.href = occ.url;
          link.hidden = false;
          if (occ.kind === 'industry') {
            link.target = '_blank';
            link.rel = 'noopener noreferrer';
          } else {
            link.removeAttribute('target');
            link.removeAttribute('rel');
          }
          if (linkLabel) linkLabel.textContent = occ.linkLabel || 'View details';
        } else {
          link.hidden = true;
        }
      }

      this.detailEl.hidden = false;
    }

    hideDetail() {
      if (this.detailEl) this.detailEl.hidden = true;
    }
  }

  function escapeHtml(str) {
    return String(str || '')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  function cssEscape(str) {
    return String(str || '').replace(/(["\\])/g, '\\$1');
  }

  if (!customElements.get('events-calendar')) {
    customElements.define('events-calendar', EventsCalendar);
  }
})();
